/**
 * User & Role management API service (admin operations).
 */

import api from "@/services/api";
import type { ModuleAccessState, PaginatedResponse, Role, User } from "@/types";

export interface UserListParams {
  query?: string;
  is_active?: boolean;
  role?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface UpdateProfilePayload {
  full_name?: string;
  phone_number?: string;
}

export const userService = {
  async getMyProfile(): Promise<User> {
    const { data } = await api.get<User>("/users/me");
    return data;
  },

  async updateMyProfile(payload: UpdateProfilePayload): Promise<User> {
    const { data } = await api.patch<User>("/users/me", payload);
    return data;
  },

  async list(params: UserListParams): Promise<PaginatedResponse<User>> {
    const { data } = await api.get<PaginatedResponse<User>>("/users", { params });
    return data;
  },

  async getById(userId: string): Promise<User> {
    const { data } = await api.get<User>(`/users/${userId}`);
    return data;
  },

  async activate(userId: string): Promise<User> {
    const { data } = await api.patch<User>(`/users/${userId}/activate`);
    return data;
  },

  async deactivate(userId: string): Promise<User> {
    const { data } = await api.patch<User>(`/users/${userId}/deactivate`);
    return data;
  },

  async remove(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  async resetPassword(userId: string, newPassword: string): Promise<void> {
    await api.post(`/users/${userId}/reset-password`, { new_password: newPassword });
  },

  async assignRoles(userId: string, roleIds: string[]): Promise<User> {
    const { data } = await api.post<User>(`/users/${userId}/roles`, { role_ids: roleIds });
    return data;
  },
  async getModuleAccess(userId: string): Promise<ModuleAccessState[]> {
    const { data } = await api.get<{ items: ModuleAccessState[] }>(`/users/${userId}/modules`);
    return data.items;
  },
  async setModuleAccess(userId: string, modules: Record<string, boolean>): Promise<ModuleAccessState[]> {
    const { data } = await api.put<{ items: ModuleAccessState[] }>(`/users/${userId}/modules`, { modules });
    return data.items;
  },
};

export const roleService = {
  async list(): Promise<Role[]> {
    const { data } = await api.get<Role[]>("/roles");
    return data;
  },

  async create(name: string, description?: string): Promise<Role> {
    const { data } = await api.post<Role>("/roles", { name, description });
    return data;
  },

  async update(roleId: string, payload: { name?: string; description?: string }): Promise<Role> {
    const { data } = await api.patch<Role>(`/roles/${roleId}`, payload);
    return data;
  },

  async remove(roleId: string): Promise<void> {
    await api.delete(`/roles/${roleId}`);
  },
};

export default userService;
